import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Badge, Button } from "@tellescope/design-system";
import { formatDateTime } from "@tellescope/utils";
import type { FormResponse } from "@tellescope/types-models";
import { FormAnswer } from "./FormAnswer";
import { getEnduserVisibleResponses } from "../../../utils/getEnduserVisibleResponses";

interface SubmittedFormResponseProps {
  formResponse: FormResponse;
}

/** Read-only view of a submitted response, limited to the fields shared with the enduser when any are. */
export const SubmittedFormResponse = ({ formResponse }: SubmittedFormResponseProps) => {
  const { t } = useTranslation("forms");

  const visibleResponses = getEnduserVisibleResponses(formResponse.responses);

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/documents">
          <Button variant="ghost" size="sm" iconOnly className="h-8 w-8 p-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold truncate">{formResponse.formTitle}</h1>
      </div>

      <div className="bg-background rounded-lg border overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/30">
          {formResponse.submittedAt && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span>{t("Submitted {{timestamp}}", { timestamp: formatDateTime(formResponse.submittedAt) })}</span>
            </div>
          )}

          {formResponse.scores && formResponse.scores.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formResponse.scores.map((score, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {score.title}: {score.value}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div className="p-4">
          {visibleResponses.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <p className="text-sm">{t("No responses recorded")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {visibleResponses.map((response, idx) => (
                <FormAnswer
                  key={response.fieldId || response.externalId || idx}
                  title={response.fieldTitle}
                  answer={response.answer}
                  answerIsHTML={response.answerIsHTML}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
